import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { difference } from 'lodash';
import produce from 'immer';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Checkbox, IconButton, Tooltip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import InsertLinkIcon from '@mui/icons-material/InsertLink';

import HistoryIcon from '@mui/icons-material/History';
import { SelectCustom } from '../../../../components/Inputs';
import PriceTableRow from './PriceTableRow';
import localization from '../../../../localization';
import { countriesSelector, currenciesSelector } from '../../../../redux/selectors/SessionData';
import { checkRequiredPricesError, getCurrencies } from './util';
import { PRICE_PROPERTIES } from './constants';

const PricesTable = ({
  currentPrices = [],
  prices = [],
  setCurrentPrices,
  currentProductData,
  setPriceFromSource,
}) => {
  const currencyOptions = useSelector(currenciesSelector);
  const countryOptions = useSelector(countriesSelector);
  const currencies = getCurrencies(currentPrices);

  const checkAll = currentPrices.every((currentPrice) => currentPrice[PRICE_PROPERTIES.VAT_INCLUDED]);

  const pricesRequiredError = checkRequiredPricesError(currentPrices, currentProductData);

  const handleCheckAll = () => {
    setCurrentPrices(produce((draft) => {
      draft.forEach((state) => {
        state[PRICE_PROPERTIES.VAT_INCLUDED] = !checkAll;
      });
    }));
  };

  const handleNewCurrencySelect = (e) => {
    const newCurrency = e.target.value;

    setCurrentPrices(produce((draft) => {
      const usedCountries = draft
        .filter(({ currency }) => currency === newCurrency)
        .map(({ country }) => country);

      const availableCountries = difference(
        [undefined, ...countryOptions.map(({ id }) => id)],
        usedCountries,
      );

      draft.push({
        currency: newCurrency,
        country: availableCountries[0],
        startDate: new Date().getTime(),
        uid: uuidv4(),
        vatIncluded: false,
      });
    }));
  };

  return (
    <>
      <Box p={3} display="flex" alignItems="center">
        <Box width="25%" sx={{ mr: 'auto' }}>
          <SelectCustom
            name="currency"
            label="currency"
            selectOptions={currencyOptions}
            onChangeSelect={handleNewCurrencySelect}
            hasError={pricesRequiredError}
            helperText={pricesRequiredError && localization.t('errorNotifications.required')}
            isRequired={pricesRequiredError}
          />
        </Box>
        {setPriceFromSource('prev') && (
          <Tooltip
            disableInteractive
            title={localization.t('labels.clickToResetPrices')}
          >
            <IconButton
              color="secondary"
              onClick={setPriceFromSource('prev')}
              size="large"
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        )}
        {setPriceFromSource('parent') && (
          <Tooltip
            disableInteractive
            title={localization.t('labels.clickToInheritPrices')}
          >
            <IconButton
              color="secondary"
              onClick={setPriceFromSource('parent')}
              size="large"
            >
              <InsertLinkIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {currentPrices.length > 0 &&
        (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow style={{ background: '#eee' }}>
                  <TableCell align="center"><Box pb="42px">{localization.t('labels.currency')}</Box></TableCell>
                  <TableCell align="center" sx={{ minWidth: 250 }}><Box pb="42px">{localization.t('labels.country')}</Box></TableCell>
                  <TableCell align="center" sx={{ minWidth: 150 }}><Box pb="42px">{localization.t('labels.price')}</Box></TableCell>
                  <TableCell align="center" sx={{ minWidth: 150 }}><Box pb="42px">{localization.t('labels.msrp')}</Box></TableCell>
                  <TableCell align="center" sx={{ minWidth: 150 }}>
                    <Box pb="42px">{localization.t('labels.upsellPrice')}</Box>
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 150 }}>
                    <Box pb="42px">
                      {localization.t('labels.crossSellPrice')}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 260 }}><Box pb="42px">{localization.t('labels.startDate')}</Box></TableCell>
                  <TableCell align="center" sx={{ minWidth: 260 }}><Box pb="42px">{localization.t('labels.endDate')}</Box></TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column">
                      {localization.t('labels.vatIncluded')}
                      <Checkbox
                        checked={checkAll}
                        onChange={handleCheckAll}
                        name="vatIncluded"
                        color="primary"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {currencies.map((currency, index) => (
                  <PriceTableRow
                    key={currency}
                    index={index}
                    currency={currency}
                    currentPricesData={currentPrices}
                    setCurrentPrices={setCurrentPrices}
                    priceData={prices}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </>
  );
};

PricesTable.propTypes = {
  currentPrices: PropTypes.array,
  prices: PropTypes.array,
  setCurrentPrices: PropTypes.func,
  currentProductData: PropTypes.object,
  setPriceFromSource: PropTypes.func,
};

export default PricesTable;
